import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Star, Tag, MoreHorizontal, Mail, MailOpen, MessageCircle, FileText,
  Trash2, Moon, Sparkles, X, Check, KeyRound, Send, ShieldCheck,
} from 'lucide-react';
import {
  getMessages, setGuestVip, updateMessage, deleteMessage, askMiloAgent,
  getOutboundSettings, previewOutboundEmail, previewOutboundSms,
  sendEmails, sendSms, createEmailTemplate, createSmsTemplate,
} from '../../../data/api';
import {
  initials, relativeTime, guestDisplayName, channelForMessageType, CHANNEL_LABEL,
} from './ciUtils';
import PreviewRenderer from './PreviewRenderer';
import MiloInsights from './MiloInsights';
import { Loading, useToast } from './CiShared';
import './miloDrafting.css';

// Guest Communications (the renamed Inbox). Groups inbound messages by guest
// into conversations, defaults to that guest's true channel in the preview, and
// shows Milo Insights for the selection.
//
// Wired:
//   • Conversation list ← GET /api/messages (grouped by guest)
//   • Milo Insights     ← GET /api/communications/intel/guest/{id}/insights
//   • VIP star          ← POST /api/guests/{id}/vip

const FILTERS = ['All', 'Unread', 'VIP', 'Needs Reply'];

// Group raw messages into one conversation per guest. A conversation keys on
// matched_guest_id when present, else a normalized email, else the display name.
function groupConversations(messages, guestsById, guestsByEmail) {
  const map = new Map();
  for (const m of messages || []) {
    const gid = m.matched_guest_id ? String(m.matched_guest_id) : '';
    const email = String(m.email || '').trim().toLowerCase();
    const key = gid || email || `name:${m.guest_name || 'guest'}`;
    let conv = map.get(key);
    if (!conv) {
      const guest = (gid && guestsById.get(gid)) || (email && guestsByEmail.get(email)) || null;
      conv = {
        key,
        guestId: gid || (guest ? String(guest._id) : ''),
        guest,
        name: guest ? guestDisplayName(guest) : (m.guest_name || m.email || 'Guest'),
        email: email || (guest ? guest.email : ''),
        messages: [],
      };
      map.set(key, conv);
    }
    conv.messages.push(m);
  }
  // Sort each conversation's messages newest-first; sort conversations by their
  // most-recent message.
  const list = Array.from(map.values());
  for (const c of list) {
    c.messages.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    c.latest = c.messages[0];
    c.unread = c.messages.some((mm) => mm.status === 'unread');
    c.isVip = !!(c.guest && c.guest.is_vip);
  }
  list.sort((a, b) => String(b.latest?.created_at || '').localeCompare(String(a.latest?.created_at || '')));
  return list;
}

// Build the grounded ask we hand Milo. We give him the guest, the channel, and
// the exact inbound message so the draft replies to *this* conversation rather
// than a generic template. Milo's agent is read-only + grounded, so this just
// asks for prose — no writes happen.
function buildDraftQuestion({ guestName, channel, eventName, body, tone }) {
  const chan = CHANNEL_LABEL[channel] || 'message';
  const lines = [
    `Draft a warm, concise ${chan.toLowerCase()} reply from the event host to a guest named ${guestName || 'the guest'}.`,
    eventName ? `The event is "${eventName}".` : '',
    body ? `The guest wrote: "${String(body).slice(0, 600)}".` : 'The guest reached out but left no message body.',
    tone === 'suggested'
      ? 'Acknowledge them warmly and confirm any details they asked about.'
      : 'Address anything they asked, keep it friendly and on-brand, and invite them to reply with questions.',
    channel === 'sms'
      ? 'Keep it short enough for one or two SMS segments. No salutation block.'
      : 'Open with a brief greeting and close graciously.',
    'Return only the reply text the host can send — no preamble, notes, or quotes.',
  ];
  return lines.filter(Boolean).join(' ');
}

// The delight moment: a gold "M" orb that breathes/pulses while Milo composes,
// then the draft reveals. Pure CSS (see miloDrafting.css); reduced-motion safe.
function MiloThinkingOrb() {
  return (
    <div className="ci-think" role="status" aria-live="polite">
      <div className="ci-think-orb" aria-hidden="true"><span className="ci-think-m">M</span></div>
      <div className="ci-think-txt">
        <b>Milo</b> is composing a reply
        <span className="ci-think-dots"><span>.</span><span>.</span><span>.</span></span>
      </div>
    </div>
  );
}

// One panel that renders every draft state so no Milo button is ever a silent
// no-op: 'thinking' → orb, 'nokey' → graceful add-your-key state, 'error' →
// retry, 'ready' → editable draft + "Use this" + "Send reply".
function MiloDraftPanel({ draft, onUse, onSend, sendInfo, onClose, onRetry }) {
  if (!draft) return null;
  const { status, text } = draft;

  if (status === 'thinking') {
    return (
      <div className="ci-draft">
        <div className="ci-draft-h">
          <Sparkles size={13} /> Milo is drafting
          <button className="ci-draft-x" onClick={onClose} title="Dismiss" aria-label="Dismiss"><X size={14} /></button>
        </div>
        <MiloThinkingOrb />
      </div>
    );
  }

  if (status === 'nokey') {
    return (
      <div className="ci-draft ci-draft-nokey">
        <div className="ci-draft-h">
          <KeyRound size={13} /> Milo drafting is off
          <button className="ci-draft-x" onClick={onClose} title="Dismiss" aria-label="Dismiss"><X size={14} /></button>
        </div>
        <div className="ci-draft-msg">
          <b>Add your Anthropic key to enable Milo drafting.</b> Once a key is configured,
          Milo will write a tailored reply to this guest right here. Until then, you can still
          reply by hand or insert a template.
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="ci-draft ci-draft-nokey">
        <div className="ci-draft-h">
          <Sparkles size={13} /> Milo couldn't draft that
          <button className="ci-draft-x" onClick={onClose} title="Dismiss" aria-label="Dismiss"><X size={14} /></button>
        </div>
        <div className="ci-draft-msg">{text || 'Milo hit a snag reaching his model. Please try again in a moment.'}</div>
        <div className="ci-draft-foot">
          {onRetry && <button className="ci-btn ci-gold ci-sm" onClick={onRetry}><Sparkles size={13} /> Try again</button>}
        </div>
      </div>
    );
  }

  // status === 'ready'
  const SendIcon = sendInfo?.SendIcon || Send;
  return (
    <div className="ci-draft">
      <div className="ci-draft-h">
        <Sparkles size={13} /> Milo drafted this reply
        <button className="ci-draft-x" onClick={onClose} title="Dismiss" aria-label="Dismiss"><X size={14} /></button>
      </div>
      <textarea
        className="ci-draft-body"
        value={text}
        onChange={(e) => draft.onEdit && draft.onEdit(e.target.value)}
        aria-label="Milo's drafted reply (editable)"
      />
      <div className="ci-draft-foot">
        <span className="ci-draft-note">{sendInfo?.note || 'You can edit before using it.'}</span>
        <button className="ci-btn ci-sm" onClick={onRetry} title="Draft a different version">
          <Sparkles size={13} /> Redraft
        </button>
        <button className="ci-btn ci-sm" onClick={() => onUse(text)} title="Copy this reply to your clipboard">
          <Check size={13} /> Use this
        </button>
        <button
          className="ci-btn ci-gold ci-sm"
          onClick={() => onSend(text)}
          disabled={!sendInfo?.canSend}
          title={sendInfo?.canSend ? `${sendInfo.verb} this reply to ${sendInfo.recipientLabel}` : sendInfo?.blockedReason}
        >
          <SendIcon size={13} /> {sendInfo?.verb || 'Send'} reply
        </button>
      </div>
    </div>
  );
}

// Pre-send confirmation: recipient + channel + resolved mode, then a final
// gated send. Mirrors CreateScreen's confirm modal so the host always sees
// exactly what will leave (and on which safety mode) before it does.
function SendConfirm({ info, sending, onConfirm, onCancel }) {
  if (!info) return null;
  const live = info.mode === 'live';
  return (
    <div className="ci-sendc-back" role="dialog" aria-modal="true" aria-label="Confirm reply" onClick={() => !sending && onCancel()}>
      <div className="ci-sendc" onClick={(e) => e.stopPropagation()}>
        <div className="ci-sendc-h">
          <ShieldCheck size={20} style={{ color: live ? 'var(--ci-gold-hi)' : 'var(--ci-green)', flex: 'none' }} />
          <b>Send this reply?</b>
        </div>
        <p className="ci-sendc-lead">
          {info.mode === 'live'
            ? `This sends a real ${info.channelLabel.toLowerCase()} to ${info.recipientName}.`
            : info.mode === 'test'
              ? `Test Mode is on — this records a test ${info.channelLabel.toLowerCase()} for ${info.recipientName} without contacting them.`
              : `Outbound is on Hold — this queues the ${info.channelLabel.toLowerCase()} reply to ${info.recipientName} in the Outbox for your review before anything goes out.`}
        </p>
        <div className="ci-sendc-rows">
          <div className="ci-sendc-row"><span>To</span><b>{info.recipientName}</b></div>
          <div className="ci-sendc-row"><span>Channel</span><b>{info.channelLabel}</b></div>
          <div className="ci-sendc-row"><span>{info.channel === 'sms' ? 'Number' : 'Address'}</span><b className="ci-sendc-addr">{info.address}</b></div>
          <div className="ci-sendc-row"><span>Safety mode</span><b style={{ color: live ? 'var(--ci-gold-hi)' : 'var(--ci-green)' }}>{info.modeLabel}</b></div>
        </div>
        <div className="ci-sendc-foot">
          <button className="ci-btn ci-sm" onClick={onCancel} disabled={sending}>Cancel</button>
          <button className="ci-btn ci-gold ci-sm" onClick={onConfirm} disabled={sending}>
            {sending ? 'Working…' : <><Send size={13} /> {info.verb}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InboxScreen({
  guests = [], eventName, onMessagesChanged, miloQuiet = false, onWakeMilo,
}) {
  const [messages, setMessages] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [filter, setFilter] = useState('All');
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const [vipBusy, setVipBusy] = useState(false);
  const [localVip, setLocalVip] = useState({}); // optimistic guestId -> bool
  const [msgBusy, setMsgBusy] = useState(false); // a star/flag/status/delete write is in flight
  const [menuOpen, setMenuOpen] = useState(false); // the ⋯ overflow menu on the selected header
  const menuRef = useRef(null);
  // Milo draft state for the selected conversation. null = no panel; otherwise
  // { status:'thinking'|'ready'|'nokey'|'error', text, onEdit }.
  const [draft, setDraft] = useState(null);
  const draftReq = useRef(0); // guards against a stale draft landing after a newer ask
  const { show: showToast, node: toastNode } = useToast();

  // Outbound send mode (hold/test/live) for the in-inbox "Send reply" action.
  // Read once like CreateScreen so the resolved verb (Queue/Send test/Send) and
  // the safety surface are honest before anything leaves the Hub.
  const [outSettings, setOutSettings] = useState(null);
  const [outSettingsErr, setOutSettingsErr] = useState('');
  const [sendConfirm, setSendConfirm] = useState(null); // pre-send confirmation payload, or null
  const [sending, setSending] = useState(false);
  const sendCorrelation = useRef('');

  const guestsById = useMemo(() => {
    const m = new Map();
    for (const g of guests || []) m.set(String(g._id || g.id), g);
    return m;
  }, [guests]);
  const guestsByEmail = useMemo(() => {
    const m = new Map();
    for (const g of guests || []) {
      const e = String(g.email || '').trim().toLowerCase();
      if (e) m.set(e, g);
    }
    return m;
  }, [guests]);

  const load = () => {
    setLoadErr(false);
    getMessages()
      .then((d) => setMessages(Array.isArray(d) ? d : []))
      .catch(() => { setLoadErr(true); setMessages([]); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Load the outbound send mode so "Send reply" can resolve its verb and route
  // through the same hold/test/live gate the rest of the Hub uses.
  useEffect(() => {
    let alive = true;
    getOutboundSettings()
      .then((d) => { if (alive) { setOutSettings(d); setOutSettingsErr(''); } })
      .catch((e) => { if (alive) setOutSettingsErr(e?.response?.data?.detail || 'Send settings are unavailable.'); });
    return () => { alive = false; };
  }, []);

  const conversations = useMemo(
    () => groupConversations(messages || [], guestsById, guestsByEmail),
    [messages, guestsById, guestsByEmail],
  );

  // Apply optimistic VIP overrides on top of grouped data.
  const withVip = useMemo(() => conversations.map((c) => ({
    ...c,
    isVip: c.guestId in localVip ? localVip[c.guestId] : c.isVip,
  })), [conversations, localVip]);

  const filtered = useMemo(() => {
    let list = withVip;
    if (filter === 'Unread') list = list.filter((c) => c.unread);
    else if (filter === 'VIP') list = list.filter((c) => c.isVip);
    else if (filter === 'Needs Reply') list = list.filter((c) => c.unread);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((c) =>
        c.name.toLowerCase().includes(q)
        || String(c.email || '').toLowerCase().includes(q)
        || String(c.latest?.content || '').toLowerCase().includes(q));
    }
    return list;
  }, [withVip, filter, query]);

  // Keep a valid selection as the list changes.
  useEffect(() => {
    if (!filtered.length) { setSelectedKey(null); return; }
    if (!selectedKey || !filtered.some((c) => c.key === selectedKey)) {
      setSelectedKey(filtered[0].key);
    }
  }, [filtered, selectedKey]);

  const selected = filtered.find((c) => c.key === selectedKey) || null;
  const actualChannel = selected ? channelForMessageType(selected.latest?.message_type) : 'email';

  const toggleVip = async () => {
    if (!selected || !selected.guestId || vipBusy) return;
    const next = !selected.isVip;
    setLocalVip((m) => ({ ...m, [selected.guestId]: next })); // optimistic
    setVipBusy(true);
    try {
      await setGuestVip(selected.guestId, next);
      if (onMessagesChanged) onMessagesChanged();
    } catch {
      // revert on failure
      setLocalVip((m) => ({ ...m, [selected.guestId]: !next }));
    } finally {
      setVipBusy(false);
    }
  };

  // ---- Milo drafting ------------------------------------------------------
  // Ask Milo to draft a reply to the *selected* conversation. `tone` is
  // 'reply' for the in-preview "Draft Reply with Milo" buttons and 'suggested'
  // for the right-column "Generate Reply" card. We surface a thinking orb, then
  // the draft. A missing key (refusal_reason 'no_llm_key' or a 401/403) yields a
  // graceful "add your key" panel — never a silent dead button.
  const runDraft = async (tone) => {
    if (!selected) {
      showToast('Select a conversation first, then Milo can draft a reply.');
      return;
    }
    const myReq = draftReq.current + 1;
    draftReq.current = myReq;
    setDraft({ status: 'thinking', text: '', tone });
    const question = buildDraftQuestion({
      guestName: selected.name,
      channel: actualChannel,
      eventName,
      body: selected.latest?.content,
      tone,
    });
    try {
      const res = await askMiloAgent(question);
      if (draftReq.current !== myReq) return; // a newer ask superseded this one
      const reason = res?.refusal_reason;
      if (reason === 'no_llm_key') {
        setDraft({ status: 'nokey', text: '', tone });
        return;
      }
      if (reason) {
        // any other refusal (llm_error, grounding, etc.) — show Milo's own words.
        setDraft({ status: 'error', text: res?.answer || '', tone });
        return;
      }
      const answer = String(res?.answer || '').trim();
      if (!answer) {
        setDraft({ status: 'error', text: '', tone });
        return;
      }
      setDraft({
        status: 'ready',
        text: answer,
        tone,
        onEdit: (val) => setDraft((d) => (d ? { ...d, text: val } : d)),
      });
    } catch (e) {
      if (draftReq.current !== myReq) return;
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        setDraft({ status: 'nokey', text: '', tone });
      } else {
        setDraft({ status: 'error', text: '', tone });
      }
    }
  };

  const onDraft = () => runDraft('reply');
  const onGenerateReply = () => runDraft('suggested');
  const closeDraft = () => { draftReq.current += 1; setDraft(null); };
  // "Use this" copies the draft to the clipboard so the host can paste it
  // anywhere (a secondary path to the in-inbox "Send reply" below).
  const useDraft = (text) => {
    const t = String(text || '').trim();
    if (!t) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(t).then(
        () => showToast('Draft copied — paste it wherever you like, or use “Send reply”.'),
        () => showToast('Draft ready — use “Send reply” to send it through the safety gate.'),
      );
    } else {
      showToast('Draft ready — use “Send reply” to send it through the safety gate.');
    }
  };

  // ---- Reply-and-send (through the EXISTING safety gate) ------------------
  // The conversation's TRUE channel decides routing: email/invitation → email
  // to guest.email; sms → sms to guest.phone; note → saved as an internal note
  // (no external send). `sendInfo` resolves the recipient, the resolved verb
  // (Queue/Send test/Send from send_mode), and whether Send is even possible
  // (a missing email/phone disables it with an explanatory tooltip) — so the
  // button is never a silent no-op.
  const sendChannel = actualChannel === 'invitation' ? 'email' : actualChannel; // an invitation IS an email
  const sendMode = outSettings?.send_mode || 'hold';
  const modeLabel = sendMode === 'live' ? 'Live Send' : sendMode === 'test' ? 'Test Mode' : 'Hold for Review';
  const sendVerb = sendMode === 'live' ? 'Send' : sendMode === 'test' ? 'Send test' : 'Queue';

  const sendInfo = useMemo(() => {
    if (!selected) return { canSend: false, blockedReason: 'Select a conversation first.', verb: sendVerb };
    const guest = selected.guest;
    const recipientName = selected.name || 'this guest';
    if (sendChannel === 'note') {
      return {
        canSend: true,
        channel: 'note',
        channelLabel: 'Internal note',
        verb: 'Save',
        SendIcon: FileText,
        recipientLabel: recipientName,
        recipientName,
        address: 'Event team only',
        note: 'Saves as an internal note — nothing is sent to the guest.',
      };
    }
    if (sendChannel === 'sms') {
      const phone = String(guest?.phone || '').trim();
      return {
        canSend: !!phone && !guest?.sms_opted_out && !outSettingsErr,
        channel: 'sms',
        channelLabel: 'SMS',
        verb: sendVerb,
        SendIcon: MessageCircle,
        recipientLabel: recipientName,
        recipientName,
        address: phone || 'no number on file',
        mode: sendMode,
        modeLabel,
        blockedReason: outSettingsErr
          || (!phone ? `${recipientName} has no phone number on file — can't send an SMS.`
            : guest?.sms_opted_out ? `${recipientName} has opted out of SMS.` : ''),
        note: phone ? `${sendVerb} an SMS to ${recipientName}.` : 'No phone on file for this guest.',
      };
    }
    // email (covers true email + invitation, both real outbound email)
    const email = String(guest?.email || selected.email || '').trim();
    return {
      canSend: !!email && !outSettingsErr,
      channel: 'email',
      channelLabel: 'Email',
      verb: sendVerb,
      SendIcon: Mail,
      recipientLabel: recipientName,
      recipientName,
      address: email || 'no address on file',
      mode: sendMode,
      modeLabel,
      blockedReason: outSettingsErr || (!email ? `${recipientName} has no email address on file — can't send an email.` : ''),
      note: email ? `${sendVerb} an email to ${recipientName}.` : 'No email on file for this guest.',
    };
  }, [selected, sendChannel, sendMode, sendVerb, modeLabel, outSettingsErr]);

  // Step 1: open the confirm card (recipient + channel + mode) for the reply
  // text. Notes skip the gate entirely and persist as an internal draft.
  const beginSend = (text) => {
    const body = String(text || '').trim();
    if (!selected || !body) return;
    if (!sendInfo.canSend) { showToast(sendInfo.blockedReason || 'Can’t send this reply.'); return; }
    setSendConfirm({ ...sendInfo, body });
  };

  // Step 2: the gated send. Email/SMS auto-mint an inactive template from the
  // reply (exactly like CreateScreen), preview FIRST to mint the stale-preview
  // digest, then sendEmails/sendSms with that digest — the gate is never
  // bypassed. Notes save as an inactive note template (internal only). On
  // success: toast + mark the conversation read.
  const confirmSend = async () => {
    if (!sendConfirm || sending) return;
    const info = sendConfirm;
    const body = info.body;
    setSending(true);
    sendCorrelation.current = `ci-inbox-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const correlation = sendCorrelation.current;
    const guestIds = [selected.guestId].filter(Boolean);
    try {
      // NOTE channel: internal only — persist as an inactive note template.
      if (info.channel === 'note') {
        await createEmailTemplate({
          name: `Note to ${info.recipientName} (${new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})`,
          subject: `Internal note — ${info.recipientName}`,
          template_type: 'note',
          html_content: `<p>${String(body).replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])).replace(/\n/g, '<br/>')}</p>`,
          is_active: false,
        });
        setSendConfirm(null);
        setDraft(null);
        draftReq.current += 1;
        showToast('Saved as an internal note — nothing was sent to the guest.', 4200);
        if (msgId) updateMessage(msgId, { status: 'read' }).then(() => { load(); if (onMessagesChanged) onMessagesChanged(); }).catch(() => {});
        return;
      }

      if (!guestIds.length) {
        setSending(false);
        showToast('This conversation isn’t linked to a guest record, so it can’t be sent through the gate. Use “Use this” to copy it instead.', 4600);
        return;
      }

      const isSms = info.channel === 'sms';
      const subject = `Re: your message about ${eventName || 'the event'}`;
      // 0) Mint an inactive template so the draft can flow through the gate.
      let templateId = '';
      if (isSms) {
        const created = await createSmsTemplate({
          name: `Reply to ${info.recipientName} (${new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})`,
          template_type: 'custom',
          message_text: body,
        });
        templateId = String(created?.id || created?._id || '');
      } else {
        const created = await createEmailTemplate({
          name: `Reply to ${info.recipientName} (${new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})`,
          subject,
          template_type: 'custom',
          html_content: `<p>${String(body).replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])).replace(/\n/g, '<br/>')}</p>`,
          is_active: false,
        });
        templateId = String(created?.id || created?._id || '');
      }
      if (!templateId) {
        setSending(false);
        showToast('Could not prepare this reply to send. Try “Use this” to copy it instead.', 4600);
        return;
      }
      // 1) Preview FIRST to mint the stale-preview digest the gate requires.
      const previewPayload = {
        template_id: templateId,
        guest_ids: guestIds,
        custom_body: body,
        correlation_id: correlation,
        source: 'communications_inbox_reply',
      };
      const preview = isSms
        ? await previewOutboundSms(previewPayload)
        : await previewOutboundEmail({ ...previewPayload, custom_subject: subject });
      if (preview?.error) {
        setSending(false);
        showToast(preview.error, 4600);
        return;
      }
      const digest = preview?.preview_digest;
      // 2) Send through the SAME safe path CreateScreen uses (respects send_mode).
      const result = isSms
        ? await sendSms(templateId, guestIds, {
          custom_body: body,
          correlation_id: correlation,
          preview_digest: digest,
          source: 'communications_inbox_reply',
        })
        : await sendEmails(templateId, guestIds, [], [], true, {
          custom_subject: subject,
          custom_body: body,
          correlation_id: correlation,
          preview_digest: digest,
          source: 'communications_inbox_reply',
        });
      if (result?.status === 'failed') {
        setSending(false);
        showToast(result?.message || 'The reply could not be sent.', 4600);
        return;
      }
      // Success: clear the draft + confirm, toast the resolved outcome, and mark
      // the conversation read so the inbox reflects that it's handled.
      setSendConfirm(null);
      setDraft(null);
      draftReq.current += 1;
      const okMsg = result?.message
        || (sendMode === 'live' ? `Reply sent to ${info.recipientName}.`
          : sendMode === 'test' ? `Test reply recorded for ${info.recipientName} (no real message sent).`
            : `Reply queued in the Outbox for ${info.recipientName} — review it there before it goes out.`);
      showToast(okMsg, 4200);
      if (msgId) updateMessage(msgId, { status: 'read' }).then(() => { load(); if (onMessagesChanged) onMessagesChanged(); }).catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail || 'The reply could not be sent right now.';
      showToast(detail, 4600);
    } finally {
      setSending(false);
    }
  };

  // Drop any open draft when the host switches conversations — a draft for one
  // guest must never linger over another. Also dismiss a pending send confirm.
  useEffect(() => { setDraft(null); draftReq.current += 1; setSendConfirm(null); }, [selectedKey]);

  // The message the header acts on is the conversation's most-recent one.
  const latestMsg = selected?.latest || null;
  const msgId = latestMsg?.id || null;
  const isHighlighted = !!latestMsg?.highlighted;
  const isFlagged = !!latestMsg?.flagged;
  const isUnread = latestMsg?.status === 'unread';

  // Patch the selected message, then refetch so the list + preview reflect the
  // new truth. Errors are swallowed (the reload restores real state); a busy
  // flag prevents overlapping writes. Returns a promise so callers can chain.
  const patchSelected = async (patch) => {
    if (!msgId || msgBusy) return;
    setMsgBusy(true);
    try {
      await updateMessage(msgId, patch);
      load();
      if (onMessagesChanged) onMessagesChanged();
    } catch {
      /* reload below restores truth on next interaction; nothing to revert */
    } finally {
      setMsgBusy(false);
    }
  };

  const toggleHighlight = () => patchSelected({ highlighted: !isHighlighted });
  const toggleFlag = () => patchSelected({ flagged: !isFlagged });
  const markRead = () => { setMenuOpen(false); patchSelected({ status: isUnread ? 'read' : 'unread' }); };

  const removeMessage = async () => {
    if (!msgId || msgBusy) return;
    setMenuOpen(false);
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    setMsgBusy(true);
    try {
      await deleteMessage(msgId);
      load();
      if (onMessagesChanged) onMessagesChanged();
    } catch {
      /* leave the list as-is; the message stays visible if the delete failed */
    } finally {
      setMsgBusy(false);
    }
  };

  // Close the ⋯ menu on any outside click while it's open.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // Drop the menu whenever the selected conversation changes.
  useEffect(() => { setMenuOpen(false); }, [selectedKey]);

  if (messages === null) {
    return <div className="ci-view"><Loading label="Loading conversations…" /></div>;
  }

  return (
    <div className="ci-view">
      <div className="ci-inbox3">
        {/* LEFT — conversation list */}
        <div className="ci-col">
          <div className="ci-col-h"><b style={{ fontSize: 13 }}>Smart Filters</b></div>
          <div className="ci-filt">
            {FILTERS.map((f) => (
              <button key={f} className={`ci-fchip ${filter === f ? 'ci-on' : ''}`} onClick={() => setFilter(f)}>{f}</button>
            ))}
          </div>
          <div className="ci-search">
            <Search size={14} color="var(--ci-txt3)" />
            <input
              placeholder="Search conversations…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="ci-conv-list">
            {loadErr && <div className="ci-empty">Couldn't load messages. <button className="ci-btn ci-sm" onClick={load} style={{ marginTop: 8 }}>Retry</button></div>}
            {!loadErr && filtered.length === 0 && (
              <div className="ci-empty">{messages.length === 0 ? 'No guest messages yet. As guests reply, their conversations appear here.' : 'No conversations match this filter.'}</div>
            )}
            {filtered.map((c) => (
              <div
                key={c.key}
                className={`ci-conv ${c.key === selectedKey ? 'ci-on' : ''}`}
                onClick={() => setSelectedKey(c.key)}
              >
                <div className="ci-av">{initials(c.name)}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="ci-nm">
                    {c.name}
                    {c.isVip && <span className="ci-vip">VIP</span>}
                    {c.latest?.highlighted && <Star size={12} fill="currentColor" style={{ color: 'var(--ci-gold-hi)', flex: 'none' }} aria-label="Highlighted" />}
                    {c.latest?.flagged && <Tag size={12} fill="currentColor" style={{ color: 'var(--ci-gold-hi)', flex: 'none' }} aria-label="Flagged" />}
                    <span className="ci-tm">{relativeTime(c.latest?.created_at)}</span>
                  </div>
                  <div className="ci-pv">{c.latest?.content || 'No preview available.'}</div>
                </div>
                {c.unread && <div className="ci-dot" />}
              </div>
            ))}
          </div>
        </div>

        {/* CENTER — preview canvas */}
        <div className="ci-col">
          <div className="ci-canv-h">
            <div>
              <div className="ci-nm">
                <span>{selected ? selected.name : 'No conversation selected'}</span>
                {selected?.isVip && <span className="ci-vip">VIP</span>}
              </div>
              <div className="ci-sub">
                {selected ? `${CHANNEL_LABEL[actualChannel]} · ${relativeTime(selected.latest?.created_at) || 'recently'}` : '—'}
              </div>
            </div>
            <div className="ci-ics" style={{ position: 'relative' }}>
              <button
                className={`ci-cic ${isHighlighted ? 'ci-vipon' : ''}`}
                title={selected ? (isHighlighted ? 'Remove highlight' : 'Highlight this message') : 'No message to highlight'}
                onClick={toggleHighlight}
                disabled={!msgId || msgBusy}
              >
                <Star size={15} fill={isHighlighted ? 'currentColor' : 'none'} />
              </button>
              <button
                className={`ci-cic ${isFlagged ? 'ci-vipon' : ''}`}
                title={selected ? (isFlagged ? 'Remove flag' : 'Flag for follow-up') : 'No message to flag'}
                onClick={toggleFlag}
                disabled={!msgId || msgBusy}
              >
                <Tag size={15} fill={isFlagged ? 'currentColor' : 'none'} />
              </button>
              <button
                className="ci-cic"
                title="More actions"
                onClick={() => setMenuOpen((o) => !o)}
                disabled={!msgId}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <MoreHorizontal size={15} />
              </button>
              {menuOpen && msgId && (
                <div className="ci-menu" ref={menuRef} role="menu">
                  <button role="menuitem" onClick={markRead} disabled={msgBusy}>
                    {isUnread
                      ? <><MailOpen size={13} style={{ verticalAlign: -2, marginRight: 7 }} /> Mark as read</>
                      : <><Mail size={13} style={{ verticalAlign: -2, marginRight: 7 }} /> Mark as unread</>}
                  </button>
                  {selected?.guestId && (
                    <button role="menuitem" onClick={() => { setMenuOpen(false); toggleVip(); }} disabled={vipBusy}>
                      <Star size={13} style={{ verticalAlign: -2, marginRight: 7 }} fill={selected.isVip ? 'currentColor' : 'none'} />
                      {selected.isVip ? 'Unmark VIP' : 'Mark as VIP'}
                    </button>
                  )}
                  <button role="menuitem" onClick={removeMessage} disabled={msgBusy}>
                    <Trash2 size={13} style={{ verticalAlign: -2, marginRight: 7 }} /> Delete message
                  </button>
                </div>
              )}
            </div>
          </div>
          {selected ? (
            <>
              <PreviewRenderer
                message={selected.latest}
                guest={selected.guest}
                actualChannel={actualChannel}
                eventName={eventName}
                onDraft={onDraft}
                draftBusy={draft?.status === 'thinking'}
              />
              <MiloDraftPanel
                draft={draft}
                onUse={useDraft}
                onSend={beginSend}
                sendInfo={sendInfo}
                onClose={closeDraft}
                onRetry={() => runDraft(draft?.tone || 'reply')}
              />
            </>
          ) : (
            <div className="ci-empty" style={{ padding: 40 }}>Select a conversation on the left to preview it as the guest received it.</div>
          )}
        </div>

        {/* RIGHT — Milo Insights (hidden while Milo is resting) */}
        <div className="ci-col">
          {miloQuiet ? (
            <div className="ci-insights">
              <div className="ci-ins-h"><Moon size={15} /> Milo is resting</div>
              <div className="ci-milo-rest">
                <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--ci-txt2)', lineHeight: 1.5 }}>
                  Insights and recommendations are paused. Turn Milo on to see his read on each guest.
                </p>
                {onWakeMilo && (
                  <button className="ci-btn ci-gold ci-sm" onClick={onWakeMilo}>
                    <Sparkles size={13} /> Turn Milo on
                  </button>
                )}
              </div>
            </div>
          ) : (
            <MiloInsights
              guestId={selected?.guestId}
              guestName={selected?.name}
              onGenerateReply={selected ? onGenerateReply : undefined}
              generating={draft?.tone === 'suggested' && draft?.status === 'thinking'}
            />
          )}
        </div>
      </div>
      <SendConfirm
        info={sendConfirm}
        sending={sending}
        onConfirm={confirmSend}
        onCancel={() => { if (!sending) setSendConfirm(null); }}
      />
      {toastNode}
    </div>
  );
}
